import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDMY } from "@/lib/date";
import { pickInitialSeasonId, saveSeasonPref } from "@/lib/seasonPref";
import { Plus, Pencil, Trash2, X, Play, Printer, Upload, Share2, Check, Lock, BarChart3 } from "lucide-react";
import { WhatsAppIcon } from "@/components/PublicShareCard";
import { AggregateLeaderboardModal } from "@/components/competitions/AggregateLeaderboardModal";
import { AggregatePresetsMenu } from "@/components/competitions/AggregatePresetsMenu";

const TYPES = ["PDI Colour", "PDI Mono", "Print Colour", "Print Mono", "Themed", "Open"];
const CATEGORIES = ["Beginner", "Intermediate", "Advanced", "Open"];

export default function Competitions() {
  const { user } = useAuth();
  const canEdit = ["ADMIN", "SECRETARY"].includes(user?.role);
  const canDelete = user?.role === "ADMIN";

  const [list, setList] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [seasonFilter, setSeasonFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = {};
    if (seasonFilter) params.season_id = seasonFilter;
    if (typeFilter) params.competition_type = typeFilter;
    const { data } = await api.get("/competitions", { params });
    setList(data);
  };

  useEffect(() => { load(); }, [seasonFilter, typeFilter]);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/seasons");
      setSeasons(data);
      if (!seasonFilter) setSeasonFilter(pickInitialSeasonId(data));
    })();
  }, []);

  // Club-wide share token — needed so per-competition share links resolve to
  // the correct public portal page instead of the "missing token" error.
  // Only admins/secretaries see the Competitions page; both roles can hit
  // /clubs/me/share (backend allows any signed-in member of the tenant).
  const [shareToken, setShareToken] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/clubs/me/share");
        setShareToken(data.share_token || "");
      } catch (_) {
        // If the share endpoint fails we still want the page to render — the
        // share button will just fall back to a token-less URL (which the
        // portal will politely tell the visitor is invalid).
      }
    })();
  }, []);

  const seasonName = (id) => seasons.find((s) => s.id === id)?.season_name || "—";

  // Tracks which competition's share link was just copied so we can flash
  // a tick on the share button for visual confirmation.
  const [sharedId, setSharedId] = useState("");
  const buildShareUrl = (c) =>
    shareToken
      ? `${window.location.origin}/portal/competition/${c.id}?token=${shareToken}`
      : `${window.location.origin}/portal/competition/${c.id}`;

  const copyShareLink = async (c) => {
    const url = buildShareUrl(c);
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch (_) {
      // Fallback for older browsers / non-secure contexts.
      try {
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta); ta.select();
        copied = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (__) { /* leave copied=false */ }
    }
    setSharedId(c.id);
    setTimeout(() => setSharedId(""), 1800);

    if (copied) {
      toast.success(`Link copied — "${c.competition_name}"`, {
        description: url,
        duration: 4000,
      });
    } else {
      // Prompt as a last-resort fallback so the user can copy manually.
      toast.error("Couldn't copy automatically — copy the link from the box that just opened.");
      window.prompt("Copy this public results link:", url);
    }
  };

  // Default order: newest first by competition date.
  const sortedList = React.useMemo(() => {
    return [...list].sort((a, b) =>
      (b.competition_date || "").localeCompare(a.competition_date || "")
    );
  }, [list]);

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.competition_name}" and all its entries?`)) return;
    await api.delete(`/competitions/${c.id}`);
    load();
  };

  // Ad-hoc "aggregate scores across N competitions" — a Set of comp ids the
  // user has ticked. Cleared automatically when the season filter changes so
  // stale selections don't accidentally leak across seasons. The
  // `skipFilterReset` ref suppresses that clear for a single filter
  // change immediately after a preset is applied (the preset itself
  // may need to clear the filter to reveal a cross-season selection).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [aggregateOpen, setAggregateOpen] = useState(false);
  const skipFilterReset = useRef(false);
  useEffect(() => {
    if (skipFilterReset.current) {
      skipFilterReset.current = false;
      return;
    }
    setSelectedIds(new Set());
  }, [seasonFilter, typeFilter]);
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectedCount = selectedIds.size;
  const canAggregate = selectedCount >= 2;

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="ccs-label mb-2">Events</div>
          <h1 className="text-4xl font-semibold tracking-tight">Competitions</h1>
        </div>
        {canEdit && (
          <button
            className="ccs-btn"
            onClick={() =>
              setEditing({
                competition_name: "",
                competition_date: new Date().toISOString().slice(0, 10),
                // When creating a new competition, prefer the season
                // currently filtered in the list; else the active season
                // (matches Season Stats etc); else fall back to the
                // first available. Avoids the "why did my new comp go
                // to 2019/20?" surprise on first click.
                season_id: seasonFilter || seasons.find((s) => s.is_active)?.id || seasons[0]?.id || "",
                judge_name: "",
                competition_type: "Open",
                category: "Open",
                round_number: 1,
                notes: "",
                score_min: 0,
                score_max: 999,
                excluded_from_season_total: false,
              })
            }
            data-testid="add-competition-button"
          >
            <Plus size={16} /> New competition
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select
          className="ccs-input" style={{ width: 220 }}
          value={seasonFilter} onChange={(e) => { setSeasonFilter(e.target.value); saveSeasonPref(e.target.value); }}
          data-testid="competition-season-filter"
        >
          <option value="">All seasons</option>
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>{s.season_name}</option>
          ))}
        </select>
        <select
          className="ccs-input" style={{ width: 180 }}
          value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <div className="ml-auto flex items-center gap-2">
          {selectedCount > 0 && (
            <span className="muted-text text-xs" data-testid="aggregate-selection-count">
              {selectedCount} selected
              <button
                type="button"
                onClick={clearSelection}
                className="ml-2 underline hover:text-white"
                data-testid="aggregate-clear-selection"
              >
                clear
              </button>
            </span>
          )}
          <AggregatePresetsMenu
            selectedIds={selectedIds}
            onApply={({ competitionIds, openModal }) => {
              // Presets may reference comps outside the current filter
              // (e.g. a "cross-season" preset while filtered to one
              // season). Clear the filter if that's the case so the
              // ticks are visible — but suppress the filter-change
              // wipeout of selectedIds for this one transition.
              const missingUnderFilter = competitionIds.some(
                (id) => !list.some((c) => c.id === id),
              );
              if (missingUnderFilter) {
                skipFilterReset.current = true;
                setSeasonFilter("");
                setTypeFilter("");
              }
              setSelectedIds(new Set(competitionIds));
              if (openModal) setAggregateOpen(true);
            }}
          />
          <button
            type="button"
            disabled={!canAggregate}
            onClick={() => setAggregateOpen(true)}
            className="ccs-btn-ghost flex items-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="aggregate-scores-button"
            title={canAggregate
              ? "Combine the ticked competitions into an ad-hoc top-5 leaderboard"
              : "Tick at least two competitions to aggregate their scores"}
          >
            <BarChart3 size={14} /> Aggregate scores
          </button>
        </div>
      </div>

      <div className="ccs-card p-0 overflow-x-auto">
        <table className="ccs-table">
          <thead>
            <tr>
              <th style={{ width: 40 }} title="Tick to include in an ad-hoc aggregate leaderboard"></th>
              <th>Name</th>
              <th>Date</th>
              <th>Season</th>
              <th>Type</th>
              <th>Category</th>
              <th>Judge</th>
              <th style={{ width: 200 }}></th>
            </tr>
          </thead>
          <tbody>
            {sortedList.map((c) => (
              <tr key={c.id} data-testid={`competition-row-${c.id}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.id)}
                    onChange={() => toggleSelected(c.id)}
                    className="accent-amber-400 cursor-pointer"
                    data-testid={`aggregate-select-${c.id}`}
                    title="Include in the aggregate leaderboard"
                  />
                </td>
                <td className="font-medium">
                  <div className="flex items-center gap-3">
                    <span>{c.competition_name}</span>
                    {c.order_locked && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5"
                        style={{ background: "#1a2f1a", color: "#4ade80", border: "1px solid #14532d" }}
                        title="Running order is locked — the sequence can't be reshuffled. Unlock from Live Entry to change it."
                        data-testid={`locked-badge-${c.id}`}
                      >
                        <Lock size={10} /> Locked
                      </span>
                    )}
                    {c.excluded_from_season_total && (
                      <span
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5"
                        style={{ background: "#3a2a05", color: "#fbbf24", border: "1px solid #6b4a08" }}
                        title="Excluded from season total — its scores don't roll up into the leaderboard or Photographer of the Year."
                        data-testid={`excluded-badge-${c.id}`}
                      >
                        Not counted
                      </span>
                    )}
                    <Link
                      to={`/entry/${c.id}`}
                      className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm"
                      data-testid={`enter-${c.id}`}
                      title="Open Live Entry for this competition"
                    >
                      <Play size={12} /> Enter
                    </Link>
                  </div>
                </td>
                <td className="mono muted-text">{formatDMY(c.competition_date)}</td>
                <td className="muted-text">{seasonName(c.season_id)}</td>
                <td><span className="tag">{c.competition_type}</span></td>
                <td><span className="tag">{c.category}</span></td>
                <td className="muted-text">{c.judge_name || "—"}</td>
                <td className="text-right">
                  <button
                    onClick={() => copyShareLink(c)}
                    title={sharedId === c.id ? "Link copied!" : "Copy public results link"}
                    className={"p-2 hover:bg-neutral-800 inline-flex " + (sharedId === c.id ? "text-emerald-400" : "")}
                    data-testid={`share-${c.id}`}
                  >
                    {sharedId === c.id ? <Check size={14} /> : <Share2 size={14} />}
                  </button>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(
                      `${c.competition_name} — results: ${buildShareUrl(c)}`,
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Share this competition's results via WhatsApp"
                    className="p-2 hover:bg-neutral-800 inline-flex"
                    data-testid={`whatsapp-${c.id}`}
                  >
                    <WhatsAppIcon />
                  </a>
                  <a
                    href={`/print/scoresheet/${c.id}`}
                    target="_blank" rel="noreferrer"
                    title="Printable blank scoring sheet"
                    className="p-2 hover:bg-neutral-800 inline-flex"
                    data-testid={`print-sheet-${c.id}`}
                  >
                    <Printer size={14} />
                  </a>
                  <a
                    href={`/print/results/${c.id}`}
                    target="_blank" rel="noreferrer"
                    title="Printable results report"
                    className="p-2 hover:bg-neutral-800 inline-flex text-amber-400"
                    data-testid={`print-results-${c.id}`}
                  >
                    <Printer size={14} />
                  </a>
                  {canEdit && (
                    <button onClick={() => setEditing(c)} className="p-2 hover:bg-neutral-800">
                      <Pencil size={14} />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(c)} className="p-2 hover:bg-neutral-800 text-red-400">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sortedList.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-14" data-testid="competitions-empty">
                  <div className="text-4xl mb-2">📷</div>
                  <div className="text-white text-lg font-medium mb-1">No competitions yet</div>
                  <div className="muted-text text-sm mb-4">
                    Create your first competition to start scoring on the night.
                  </div>
                  {seasons.length > 0 ? (
                    <button
                      className="ccs-btn inline-flex items-center gap-2"
                      onClick={() =>
                        setEditing({
                          competition_name: "",
                          competition_date: new Date().toISOString().slice(0, 10),
                          season_id: seasonFilter || seasons.find((s) => s.is_active)?.id || seasons[0]?.id || "",
                          judge_name: "",
                          competition_type: "Open",
                          category: "Open",
                          round_number: 1,
                          notes: "",
                          score_min: 0,
                          score_max: 999,
                          excluded_from_season_total: false,
                        })
                      }
                    >
                      <Plus size={14} /> New competition
                    </button>
                  ) : (
                    <div className="muted-text text-xs">
                      Add a season first from the <a href="/seasons" className="text-amber-400 hover:underline">Seasons</a> page.
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <CompetitionDialog
          initial={editing}
          seasons={seasons}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {aggregateOpen && (
        <AggregateLeaderboardModal
          competitionIds={Array.from(selectedIds)}
          onClose={() => setAggregateOpen(false)}
        />
      )}
    </div>
  );
}

function CompetitionDialog({ initial, seasons, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const isEdit = !!initial.id;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        competition_name: form.competition_name,
        competition_date: form.competition_date,
        season_id: form.season_id,
        judge_name: form.judge_name || "",
        competition_type: form.competition_type,
        category: form.category,
        round_number: Number(form.round_number) || 1,
        notes: form.notes || "",
        // score_min / score_max are legacy fields — no longer enforced anywhere.
        // Keep them on the payload for schema back-compat with older records.
        score_min: 0,
        score_max: 999,
        excluded_from_season_total: !!form.excluded_from_season_total,
      };
      if (isEdit) await api.patch(`/competitions/${initial.id}`, payload);
      else await api.post("/competitions", payload);
      onSaved();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <form className="dialog-panel p-6" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-semibold">{isEdit ? "Edit" : "New"} competition</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="form-row">
          <label className="ccs-label">Competition name *</label>
          <input
            required className="ccs-input"
            value={form.competition_name}
            onChange={(e) => setForm({ ...form, competition_name: e.target.value })}
            data-testid="competition-name-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="form-row">
            <label className="ccs-label">Date *</label>
            <input type="date" required className="ccs-input"
              value={form.competition_date}
              onChange={(e) => setForm({ ...form, competition_date: e.target.value })} />
          </div>
          <div className="form-row">
            <label className="ccs-label">Season *</label>
            <select required className="ccs-input"
              value={form.season_id}
              onChange={(e) => setForm({ ...form, season_id: e.target.value })}
              data-testid="competition-season-select"
            >
              <option value="">Select…</option>
              {seasons.map((s) => <option key={s.id} value={s.id}>{s.season_name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="ccs-label">Type</label>
            <select className="ccs-input"
              value={form.competition_type}
              onChange={(e) => setForm({ ...form, competition_type: e.target.value })}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="ccs-label">Category</label>
            <select className="ccs-input"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="ccs-label">Round #</label>
            <input type="number" min="1" className="ccs-input ccs-input-mono"
              value={form.round_number}
              onChange={(e) => setForm({ ...form, round_number: e.target.value })} />
          </div>
          <div className="form-row">
            <label className="ccs-label">Judge</label>
            <input className="ccs-input"
              value={form.judge_name || ""}
              onChange={(e) => setForm({ ...form, judge_name: e.target.value })} />
          </div>
        </div>

        <div className="form-row">
          <label className="ccs-label">Notes</label>
          <textarea className="ccs-input" rows="2"
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <div className="form-row">
          <label
            className="flex items-start gap-2 cursor-pointer select-none"
            title="When ON, entries in this competition are NOT added to the season leaderboard, Photographer of the Year, multi-season trends, or photographer-profile lifetime totals. The competition's own results, scoresheet and public share link still work as normal."
          >
            <input
              type="checkbox"
              checked={!!form.excluded_from_season_total}
              onChange={(e) => setForm({ ...form, excluded_from_season_total: e.target.checked })}
              className="accent-amber-400 mt-1"
              data-testid="excluded-from-season-checkbox"
            />
            <div>
              <div className="text-sm">Exclude from season total</div>
              <div className="text-xs muted-text">
                Friendly / inter-club / external rounds whose marks shouldn&rsquo;t roll up into Photographer of the Year.
              </div>
            </div>
          </label>
        </div>

        {err && <div className="danger-text text-sm">{err}</div>}

        <div className="flex justify-end gap-3 mt-5">
          <button type="button" onClick={onClose} className="ccs-btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="ccs-btn" data-testid="save-competition">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
